import { CommonModule, formatDate } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { map } from 'rxjs/operators';
import { NotificationModule, NotificationService } from '@progress/kendo-angular-notification';
import { ReportJobService, ReportJobConfig } from 'src/app/core/services/reporting/report-job.service';
import { DropDownButtonComponent } from '@progress/kendo-angular-buttons';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { BaseComponent } from 'src/app/core/shared/common/base.component';
import { infoCircleIcon, moreVerticalIcon } from '@progress/kendo-svg-icons';
import { TicketsDispositionData } from 'src/app/core/models/analytics/soc-models';
import { TicketDataDialogComponent } from 'src/app/core/components/ticket-data-dialog';
import { TicketDataDialogConfig, ContextMenuEvent } from 'src/app/core/models/dialog-config';
import { QueryType } from 'src/app/core/services/analytics/soc-analytics.service';
import { environment } from 'src/environments/environment';
import { TooltipModule } from '@progress/kendo-angular-tooltip';
import { checkIsDemoOrg } from 'src/app/core/models/organizations';

@Component({
  standalone: true,
  selector: 'app-ticket-disposition-grid',
  imports: [CommonModule, DropDownButtonComponent, DropDownListModule, IconsModule, TicketDataDialogComponent, TooltipModule, NotificationModule],
  templateUrl: './ticket-disposition-grid.component.html',
  styleUrl: './ticket-disposition-grid.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketDispositionGridComponent extends BaseComponent {
  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public ticketDialogConfig = signal<TicketDataDialogConfig | null>(null);
  public showTicketDialog = signal(false);

  /** Injected services for server-side CSV export via report-job pipeline */
  private http = inject(HttpClient);
  private reportJobService = inject(ReportJobService);
  private notificationService = inject(NotificationService);

  /** Whether the Download CSV button is offered (opt-in per context; off by default). */
  enableCsvExport = input(false);

  /** Whether a CSV export is currently being queued */
  public isExporting = signal(false);

  /** Resolved report template id for this widget's report type */
  private reportTemplateId = signal<number | null>(null);

  /** SOC report type key for this widget (intrinsic to the widget). */
  private readonly reportKey = 'ticket-dispositions-soc-report';

  ticketsDispositionData = input.required<TicketsDispositionData[]>();
  dateRange = input<string[] | null>(null);
  organizationId = input<number | null>();
  startDate = input<string | null>();
  endDate = input<string | null>();
  queryType = input<QueryType>(QueryType.All);
  selectedOrgIds = input<number[]>([]);

  /** Optional array of column field names to display in ticket dialogs. If not provided, all columns are shown. */
  ticketVisibleColumns = input<string[]>();

  /** Optional security tool filter - comma-separated list of product IDs */
  securityTool = input<string>();

  /** Whether the ticket dialog shows the Export to Excel button. Defaults to true. */
  showExcelExport = input<boolean>(true);

  // Computed property to format counts
  formattedDispositions = computed(() => {
    return this.ticketsDispositionData().map((item) => ({
      ...item,
      formattedCount: this.formatCount(item.count),
      formattedPercentage: Number(item.percentage).toFixed(1),
    }));
  });

  constructor() {
    super();
    this.loadReportTemplateId();
  }

  /**
   * Resolve the report template id for this widget's report type key.
   */
  private loadReportTemplateId(): void {
    const url = `${environment.apiUrl}/v2/reporting/api/report-types/?$filter=key eq '${this.reportKey}'`;
    this.http
      .get<{ value: any[] }>(url)
      .pipe(map((response) => response.value?.[0]?.report_template?.id ?? null))
      .subscribe({
        next: (id) => this.reportTemplateId.set(id),
        error: (err) => console.error('Failed to load report template:', err),
      });
  }

  /**
   * Handles context menu selection for viewing data
   */
  onContextMenuSelect(event: ContextMenuEvent, stage: string): void {
    if (!event?.text) {
      console.warn('Invalid event structure:', event);
      return;
    }

    if (event.text === 'View Ticket Data') {
      this.openTicketDialog(stage);
    }
  }

  /**
   * Opens the ticket data dialog with configuration
   */
  private openTicketDialog(stage: string): void {
    let demostr = checkIsDemoOrg()? "/demo": "";
    const config: TicketDataDialogConfig = {
      organizationId: this.organizationId() ?? null,
      startDate: this.startDate() ?? null,
      endDate: this.endDate() ?? null,
      queryType: this.queryType(),
      selectedOrgIds: this.selectedOrgIds(),
      event: 'escalations',
      apiUrl: `${environment.apiUrl}${demostr}/v1/analytics/administrative/ticketeventsdetails/`,
      useDifferentDateField: true, // Use ticket_closed_time
      visibleColumns: this.ticketVisibleColumns(),
      securityTool: this.securityTool(),
      showExcelExport: this.showExcelExport(),
      reportTypeKey: this.enableCsvExport() ? this.reportKey : undefined,
    };

    this.ticketDialogConfig.set(config);
    this.showTicketDialog.set(true);
  }

  /**
   * Handles ticket dialog close
   */
  onTicketDialogClose(): void {
    this.showTicketDialog.set(false);
    this.ticketDialogConfig.set(null);
  }

  /**
   * Queue a server-side CSV export for this widget's report type.
   */
  public triggerExport(): void {
    const templateId = this.reportTemplateId();
    const orgId = this.organizationId();
    const startDate = this.startDate();
    const endDate = this.endDate();

    if (!templateId || !orgId || !startDate || !endDate) {
      this.notificationService.show({
        content: 'Unable to export: missing organization or date range.',
        type: { style: 'warning', icon: true },
        position: { horizontal: 'right', vertical: 'top' },
        animation: { type: 'fade', duration: 400 },
        hideAfter: 5000,
      });
      return;
    }

    this.isExporting.set(true);

    const config: ReportJobConfig = {
      organizations: [orgId],
      primary_organization_id: orgId,
      report_template_id: templateId,
      start_date: formatDate(startDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
      end_date: formatDate(endDate, 'yyyy-MM-dd HH:mm:ss', 'en-US', 'UTC'),
      delivery_method: 'download',
      report_params: {
        filter_type: this.queryType(),
        ...(this.securityTool() ? { security_tool: this.securityTool()! } : {}),
      },
    };

    this.reportJobService.createReportJob(config).subscribe({
      next: () => {
        this.isExporting.set(false);
        this.notificationService.show({
          content: 'Export queued. You will find the CSV in the Reports history once generated.',
          type: { style: 'success', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          closable: true,
          hideAfter: 5000,
        });
      },
      error: () => {
        this.isExporting.set(false);
        this.notificationService.show({
          content: 'Failed to queue export. Please try again.',
          type: { style: 'error', icon: true },
          position: { horizontal: 'right', vertical: 'top' },
          animation: { type: 'fade', duration: 400 },
          hideAfter: 5000,
        });
      },
    });
  }

  private formatCount(count: number): string {
    if (count >= 1000) {
      return `${(count / 1000).toFixed(1)}k`;
    }
    return count.toString();
  }

  getCountSeverity(percentage: number): string {
    if (percentage >= 30) return 'danger';
    if (percentage >= 15) return 'warning';
    return 'success';
  }
}
